/**
 * 贪吃猫狗（P3a）：输入、画面、暂停、结算的骨架。规则全在 B 的 core（createSnakeGame）；这里只把输入交进去、把状态画出来。
 * - 画面：Canvas 2D 画场地、墙、窄口、纸箱、食物、毛线球、TA 与飘字；分数、心、时间、暂停、开始页、暂停页、结算页都是 DOM，读屏念得到。
 * - 输入：在场地上点或拖（走向那个点）、方向键或 WASD、触屏左下角的摇杆；Esc 暂停。
 * - 暂停：点暂停、按 Esc、页面切到后台都会暂停；回来停在暂停页，点「继续」才接着玩。
 * - 结算：时间到、心用完了出结算页；「不玩了」直接离开，不出结算页、这局不算成绩。成绩只留在这一局里，不存。
 * - TA 的样子：有这只宠物的贪吃动作清单就按规则事件切段（F0 第 4 节），没有就画星球形象或物种插画，再没有画圆点小脸。
 * - 一只宠物一局：换了宠物整个重挂；形象、动作只认这只自己的，不串到别的宠物身上。
 * 接到哪个页面、路由怎么走由 I 接线；本组件只要一个 onExit。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CharacterState, PetPrivateSummary } from "@/shared/contracts";
import { Button, Icon } from "@/shared/ui";
import { createPlayer, currentFrame, cut, face, prepareMotion, step as stepMotion, type MotionPlayer, type MotionSet, type RuleEventId } from "@/features/pet_motion";
import { clipForEvent, createSnakeGame, eventKey, moveClipOf, type EventStamp, type SnakeConfig, type SnakeEvent, type SnakeGame as Game, type SnakeState } from "../core";
import { drawScene, readPalette, type Palette } from "./draw";
import { announcementFor, clockText, floatsFor, liveFloats, type FloatText } from "./feedback";
import { composeInput, isMoveKey, stickFromDrag } from "./input";
import { spriteFromCharacter, spriteFromFrame, spriteFromSpecies, withNaturalSize, type SpriteSpec } from "./sprite";
import { fitArena, toArena, type Fit } from "./viewport";
import "./snake.css";

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
/** 结束后先让结尾动作播一会儿再出结算页（减少动态效果时立刻出） */
export const RESULT_DELAY_MS = 900;
const JOYSTICK_RADIUS = 44;

export interface SnakeGameProps {
  pet: Pick<PetPrivateSummary, "pet_id" | "name" | "species">;
  character?: CharacterState | null;
  /** 这只宠物的贪吃动作清单（purpose: game_snake）；没有就画静态图 */
  motion?: MotionSet | null;
  seed?: number;
  durationMs?: number;
  onExit: () => void;
  /** 不给就跟随系统“减少动态效果”设置 */
  reducedMotion?: boolean;
  /** 用例与演示台换规则配置用（比如开局食物摆在哪）；产品页不传 */
  configOverrides?: Partial<SnakeConfig>;
}

type Hud = { score: number; hearts: number; clock: string };

function usePrefersReducedMotion(): boolean {
  const query = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : null;
  const [reduced, setReduced] = useState(() => query?.matches ?? false);
  useEffect(() => {
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, [query]);
  return reduced;
}

const facingOf = (radians: number) => moveClipOf(radians).slice("move_".length);

/**
 * 规则事件的标识（F0 修订 R2 第 2 条、F2）：核心给每条事件盖了 (round, step, seq)（core/events.ts，eventKey ＝ "round:step:seq"）。
 * 界面层把它原样翻成播放器认的通用信封；播放器不认识游戏类型，按这三个数比先后去重。
 */
export function ruleEventIdOf(event: EventStamp): RuleEventId {
  return { round: event.round, step: event.step, seq: event.seq };
}
const hudOf = (state: SnakeState): Hud => ({ score: state.score, hearts: state.hearts, clock: clockText(state.remainingMs) });

/**
 * 一只宠物一局：换了宠物（比如同一个页面换了路由参数）就整个重挂，分数、阶段、飘字、读屏、播放器都不带过去。
 * 同一只宠物重新下发（新对象、改了名字、形象或动作换了一版）不重开，这一局接着玩。
 */
export function SnakeGame(props: SnakeGameProps) {
  return <SnakeRound key={props.pet.pet_id} {...props} />;
}

function SnakeRound({ pet, character: givenCharacter = null, motion = null, seed, durationMs, onExit, reducedMotion, configOverrides }: SnakeGameProps) {
  // 只认这只宠物自己的形象：宠物已经换了、形象还是上一只的旧缓存时，先不画上一只的样子。
  // 动作清单不另比：没有形象就建不起来，有形象时 prepareMotion 拿这只的形象核身份（修订 D3）
  const character = givenCharacter?.pet_id === pet.pet_id ? givenCharacter : null;
  const systemReduced = usePrefersReducedMotion();
  const still = reducedMotion ?? systemReduced;
  const game = useMemo<Game>(
    () => createSnakeGame({ ...configOverrides, ...(seed !== undefined ? { seed } : { seed: Date.now() % 1_000_000 }), ...(durationMs !== undefined ? { durationMs } : {}) }),
    // 一个组件一局；再来一局用同一个对象 restart
    [],
  );
  const [phase, setPhase] = useState(game.state.phase);
  const [hud, setHud] = useState<Hud>(() => hudOf(game.state));
  const [showResult, setShowResult] = useState(false);
  const [notice, setNotice] = useState<{ seq: number; text: string }>({ seq: 0, text: "" });
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fitRef = useRef<Fit>(fitArena(0, 0, 360, 640));
  const dprRef = useRef(1);
  const paletteRef = useRef<Palette | null>(null);
  const keysRef = useRef(new Set<string>());
  const stickRef = useRef<{ dx: number; dy: number } | null>(null);
  const targetRef = useRef<{ x: number; y: number } | null>(null);
  const floatsRef = useRef<FloatText[]>([]);
  const floatSeq = useRef(0);
  const images = useRef(new Map<string, HTMLImageElement>());
  const hudRef = useRef(hud);
  const stillRef = useRef(still);
  stillRef.current = still;

  // TA 的样子：贪吃动作清单（能播才用）→ 星球形象 → 物种插画
  const prepared = useMemo(() => {
    const active = character?.active;
    return active && motion ? prepareMotion(motion, { set: active }) : null;
  }, [motion, character?.active?.pet_id, character?.active?.reference_version, character?.active?.assets.find((a) => a.pose === "neutral_full")?.content_sha256]);
  const playerRef = useRef<MotionPlayer | null>(null);
  useEffect(() => {
    playerRef.current = prepared ? createPlayer(prepared, "MOVE", facingOf(game.state.pet.facing)) : null;
  }, [prepared, game]);
  const fallbackSprite = useMemo(() => spriteFromCharacter(character) ?? spriteFromSpecies(pet.species), [character, pet.species]);
  // 动画循环与键盘、后台监听只挂一次，读当前值一律经 ref，避免拿到挂上那一刻的旧闭包
  const preparedRef = useRef(prepared);
  preparedRef.current = prepared;
  const fallbackRef = useRef(fallbackSprite);
  fallbackRef.current = fallbackSprite;
  const petNameRef = useRef(pet.name);
  petNameRef.current = pet.name;

  const loadImage = useCallback((url: string): HTMLImageElement | null => {
    if (typeof Image === "undefined") return null;
    let img = images.current.get(url);
    if (!img) {
      img = new Image();
      img.decoding = "async";
      img.src = url;
      images.current.set(url, img);
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }, []);
  // 动作帧挂上时先全部预取，走起来不现等下载
  useEffect(() => {
    for (const clip of prepared?.clips ?? []) for (const f of clip.frames) loadImage(f.url);
    if (fallbackSprite) loadImage(fallbackSprite.url);
  }, [prepared, fallbackSprite, loadImage]);

  const currentSprite = (): { spec: SpriteSpec; image: CanvasImageSource } | null => {
    const player = playerRef.current;
    const motionNow = preparedRef.current;
    if (player && motionNow) {
      const frame = currentFrame(player);
      const image = loadImage(frame.url);
      if (image) return { spec: spriteFromFrame(frame, motionNow.set.canvas), image };
    }
    const fallback = fallbackRef.current;
    if (fallback) {
      const image = loadImage(fallback.url);
      if (image) return { spec: withNaturalSize(fallback, image.naturalWidth, image.naturalHeight), image };
    }
    return null;
  };

  const draw = (nowMs: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d") ?? null;
    if (!canvas || !ctx) return;
    paletteRef.current ??= readPalette(canvas);
    floatsRef.current = liveFloats(floatsRef.current, nowMs);
    drawScene(ctx, {
      state: game.state, fit: fitRef.current, dpr: dprRef.current, palette: paletteRef.current, sprite: currentSprite(),
      floats: floatsRef.current, target: targetRef.current, nowMs, reducedMotion: stillRef.current,
    });
    stageRef.current?.setAttribute("data-motion-clip", playerRef.current?.clip?.clip_key ?? "");
    stageRef.current?.setAttribute("data-motion-frame", String(playerRef.current?.frame ?? ""));
    // 调试：最近一次切段用的是哪条规则事件（eventKey ＝ round:step:seq）
    const last = playerRef.current?.lastRuleEvent;
    stageRef.current?.setAttribute("data-motion-event", last ? eventKey({ round: last.round ?? 0, step: last.step, seq: last.seq }) : "");
  };

  const handle = (events: SnakeEvent[], nowMs: number) => {
    const state = game.state;
    for (const event of events) {
      floatsRef.current.push(...floatsFor(event, state, nowMs, () => ++floatSeq.current));
      const text = announcementFor(event, petNameRef.current);
      if (text) setNotice((n) => ({ seq: n.seq + 1, text }));
      const clip = clipForEvent(event);
      // 规则事件那一帧立即切段；帧标记只是表现，分数、命数只认规则核心（R2 第 1、3 条）
      if (clip && playerRef.current) playerRef.current = cut(playerRef.current, clip, ruleEventIdOf(event)).player;
    }
    const next = hudOf(state);
    if (next.score !== hudRef.current.score || next.hearts !== hudRef.current.hearts || next.clock !== hudRef.current.clock) {
      hudRef.current = next;
      setHud(next);
    }
    setPhase(state.phase);
  };

  // 舞台尺寸：等比放下 360×640 的场地，画布按设备像素比出清晰的图
  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!stage || !canvas) return undefined;
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      dprRef.current = dpr;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      fitRef.current = fitArena(rect.width, rect.height, 360, 640);
      draw(performance.now());
    };
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(stage);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // 主循环：只在进行中走；每帧先交输入再推进，事件当帧切段、飘字、播报
  useEffect(() => {
    if (phase !== "running" || typeof window.requestAnimationFrame !== "function") {
      draw(typeof performance !== "undefined" ? performance.now() : 0);
      return undefined;
    }
    let last: number | null = null;
    let handleId = 0;
    const tick = (now: number) => {
      if (last !== null) {
        const dt = now - last;
        const before = game.state.pet;
        game.setInput(composeInput({ stick: stickRef.current, keys: keysRef.current, target: targetRef.current }));
        const events = game.advance(dt);
        const after = game.state.pet;
        if (playerRef.current) {
          let player = face(playerRef.current, facingOf(after.facing));
          // 原地不动时走动循环停在当前帧，不原地踏步；一次性段、结尾段照常播
          const moving = after.x !== before.x || after.y !== before.y;
          if (moving || !player.clip?.loop) player = stepMotion(player, dt).player;
          playerRef.current = player;
        }
        handle(events, now);
      }
      last = now;
      draw(now);
      if (game.state.phase === "running") handleId = window.requestAnimationFrame(tick);
    };
    handleId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handleId);
  }, [phase]);

  // 结束以后：结尾动作再播一会儿，然后出结算页
  useEffect(() => {
    if (phase !== "ended" || !game.state.result?.counted) return undefined;
    if (still || typeof window.requestAnimationFrame !== "function") {
      setShowResult(true);
      return undefined;
    }
    let handleId = 0;
    let last: number | null = null;
    const startedAt = performance.now();
    const tick = (now: number) => {
      if (last !== null && playerRef.current) playerRef.current = stepMotion(playerRef.current, now - last).player;
      last = now;
      draw(now);
      if (now - startedAt >= RESULT_DELAY_MS) setShowResult(true);
      else handleId = window.requestAnimationFrame(tick);
    };
    handleId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(handleId);
  }, [phase, still]);

  const pause = () => handle(game.pause(), performance.now());
  const resume = () => handle(game.resume(), performance.now());
  const start = () => handle(game.start(), performance.now());
  const again = () => {
    setShowResult(false);
    floatsRef.current = [];
    targetRef.current = null;
    playerRef.current = preparedRef.current ? createPlayer(preparedRef.current, "MOVE", facingOf(0)) : null;
    handle(game.restart(Date.now() % 1_000_000), performance.now());
  };
  const leave = () => {
    handle(game.quit(), performance.now());
    onExit();
  };

  // 切到后台就暂停；回来停在暂停页
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && game.state.phase === "running") pause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // 键盘：方向键／WASD 走，Esc 暂停
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code === "Escape" && game.state.phase === "running") {
        e.preventDefault();
        pause();
        return;
      }
      if (!isMoveKey(e.code) || game.state.phase !== "running") return;
      e.preventDefault();
      keysRef.current.add(e.code);
    };
    const up = (e: KeyboardEvent) => keysRef.current.delete(e.code);
    const blur = () => keysRef.current.clear();
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, []);

  // 场地上点或拖：走向那个点
  const pointAt = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    targetRef.current = toArena(fitRef.current, e.clientX - rect.left, e.clientY - rect.top);
    stickRef.current = null;
  };
  const onCanvasDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (game.state.phase !== "running") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointAt(e);
  };
  const onCanvasMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (game.state.phase === "running" && e.buttons) pointAt(e);
  };

  // 摇杆：按住拖，松手停
  const knobRef = useRef<HTMLSpanElement>(null);
  const stickAt = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const stick = stickFromDrag(e.clientX - (rect.left + rect.width / 2), e.clientY - (rect.top + rect.height / 2), JOYSTICK_RADIUS);
    stickRef.current = stick;
    targetRef.current = null;
    if (knobRef.current) knobRef.current.style.transform = `translate(${stick.dx * JOYSTICK_RADIUS}px, ${stick.dy * JOYSTICK_RADIUS}px)`;
  };
  const stickRelease = () => {
    stickRef.current = null;
    if (knobRef.current) knobRef.current.style.transform = "";
  };

  const result = game.state.result;
  const hearts = Array.from({ length: Math.max(hud.hearts, 0) });
  return (
    <section className="ps-arcade" aria-label="贪吃猫狗" data-phase={phase} data-score={hud.score}>
      <header className="ps-arcade__hud">
        <span className="ps-arcade__score" aria-label={`${hud.score} 分`}>{hud.score}<small>分</small></span>
        <span className="ps-arcade__hearts" role="img" aria-label={`还剩 ${hud.hearts} 颗心`}>
          {hearts.map((_, i) => <Icon key={i} name="heart" size={16} />)}
        </span>
        <span className="ps-arcade__clock" aria-label={`还剩 ${hud.clock.replace(":", " 分 ")} 秒`}>{hud.clock}</span>
        <Button variant="ghost" size="sm" icon="pause" aria-label="暂停" onClick={pause} disabled={phase !== "running"} className="ps-arcade__pause" />
      </header>
      <div className="ps-arcade__stage" ref={stageRef}>
        <canvas
          ref={canvasRef}
          className="ps-arcade__canvas"
          role="img"
          aria-label={`${pet.name}在场地里，${hud.score} 分`}
          onPointerDown={onCanvasDown}
          onPointerMove={onCanvasMove}
        />
        {phase === "running" ? (
          <div className="ps-arcade__stick" aria-hidden="true" onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); stickAt(e); }} onPointerMove={(e) => { if (e.buttons) stickAt(e); }} onPointerUp={stickRelease} onPointerCancel={stickRelease}>
            <span ref={knobRef} className="ps-arcade__knob" />
          </div>
        ) : null}
        {phase === "ready" ? (
          <div className="ps-arcade__overlay" role="dialog" aria-modal="true" aria-labelledby="ps-arcade-title">
            <div className="ps-arcade__sheet">
              <h2 id="ps-arcade-title">贪吃猫狗</h2>
              <p className="ps-arcade__line">带{pet.name}去吃零食</p>
              {/* 一句一行、每句短到最窄的屏也放得下，不折出孤字 */}
              <ul className="ps-arcade__rules">
                <li>点或拖场地、按方向键走。</li>
                <li>零食让 TA 变大，叶子变轻。</li>
                <li>小个子才钻得过窄口。</li>
                <li>大个子才撞得开纸箱。</li>
                <li>小心毛线球，撞到少一颗心。</li>
              </ul>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="play" onClick={start} autoFocus>开始</Button>
                <Button variant="ghost" onClick={onExit}>回去</Button>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "paused" ? (
          <div className="ps-arcade__overlay" role="dialog" aria-modal="true" aria-labelledby="ps-arcade-paused">
            <div className="ps-arcade__sheet">
              <h2 id="ps-arcade-paused">暂停中</h2>
              <p className="ps-arcade__line">{hud.score} 分 · 还剩 {hud.clock}</p>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="play" onClick={resume} autoFocus>继续</Button>
                <Button variant="ghost" onClick={leave}>不玩了</Button>
              </div>
            </div>
          </div>
        ) : null}
        {phase === "ended" && showResult && result?.counted ? (
          <div className="ps-arcade__overlay is-result" role="dialog" aria-modal="true" aria-labelledby="ps-arcade-result">
            <div className="ps-arcade__sheet">
              <h2 id="ps-arcade-result">{result.reason === "time_up" ? "时间到" : "心用完了"}</h2>
              <p className="ps-arcade__total"><strong>{result.score}</strong> 分</p>
              <dl className="ps-arcade__stats">
                <div><dt>吃到</dt><dd>{result.eaten} 个</dd></div>
                <div><dt>撞开纸箱</dt><dd>{result.cratesBroken} 个</dd></div>
                <div><dt>被挡住</dt><dd>{result.blocked} 次</dd></div>
                <div><dt>被毛线球撞到</dt><dd>{result.hits} 次</dd></div>
                <div><dt>坚持了</dt><dd>{Math.round(result.durationMs / 1000)} 秒</dd></div>
                <div><dt>最大长到</dt><dd>{result.maxSize} 级</dd></div>
              </dl>
              <div className="ps-arcade__actions">
                <Button variant="primary" icon="refresh" onClick={again} autoFocus>再来一局</Button>
                <Button variant="ghost" onClick={onExit}>回去</Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <p className="visually-hidden" aria-live="polite">
        <span key={notice.seq}>{notice.text}</span>
      </p>
    </section>
  );
}
